import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../../utils/downloadExportedFiles';
import VendorGrid from './VendorGrid';
import VendorModal from './VendorModal';
import { buildVendorSavePayload } from './utils/mapVendor';
import { validateVendor } from './vendorValidation';

function VendorMain(props) {
  const [vendorList, setVendorList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [userAction, setUserAction] = useState('Add');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Vendor';

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-vendor/getAll`);
      if (response.status === 200) {
        setVendorList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load vendors!!', 'Error', setAlert);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : []);
    fetchVendors();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Vendor module');
  }, [props.Module, moduleName]);

  const openAddModal = () => {
    setUserAction('Add');
    setSelectedVendor(null);
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add vendor modal');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedVendor(null);
    setUserAction('Add');
  };

  const onSubmit = async (e, data) => {
    if (e.target.name !== 'Add' && e.target.name !== 'Edit') return;

    const payload = buildVendorSavePayload(data, getStorageValues('userId'));
    if (!validateVendor(payload, setAlert)) return;

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-vendor/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        const isAdd = e.target.name === 'Add';
        CreateAlert(
          result.message || (isAdd ? 'Vendor added successfully!!' : 'Vendor updated successfully!!'),
          'Success',
          setAlert
        );
        await fetchVendors();
        if (isAdd) {
          closeModal();
        }
      } else {
        CreateAlert(result?.message || 'Failed to save vendor!!', 'Error', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save vendor!!', 'Error', setAlert);
    }
  };

  const handleExportExcel = () => {
    const columns = [
      { name: 'Vendor Name', selector: 'VendorName' },
      { name: 'Email', selector: 'Email' },
      { name: 'Contact Number', selector: 'ContactNumber' },
      { name: 'Rating', selector: 'Rating' },
      { name: 'Review Count', selector: 'ReviewCount' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToExcel('Vendors', columns, vendorList);
  };

  const handleExportPDF = () => {
    const columns = [
      { name: 'Vendor Name', selector: 'VendorName' },
      { name: 'Email', selector: 'Email' },
      { name: 'Contact Number', selector: 'ContactNumber' },
      { name: 'Rating', selector: 'Rating' },
      { name: 'Review Count', selector: 'ReviewCount' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToPDF('Vendors', columns, vendorList);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchVendors}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <VendorGrid
        VendorData={vendorList}
        fetchVendors={fetchVendors}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <VendorModal
        show={modalIsOpen}
        UserAction={userAction}
        selectedRow={selectedVendor}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default VendorMain;
